<script setup lang="ts">
import { ref } from 'vue'
// components
import DatePickerCalendar from '@/components/date-picker/DatePickerCalendar.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import TimePicker from '@/components/forms/TimePicker.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
// types
import type { DatePreset, DateRange } from '@/types/components/dateTime'
// utilities
import { datePresets } from '@/utilities/date-time'

/** 1. Sentry-style range filter: built-in presets + one custom, absolute date with time range */
const sentryRange = ref<DateRange>({ start: null, end: null })
const sentryPresets: DatePreset[] = [
  datePresets.today,
  datePresets.yesterday,
  datePresets.last7Days,
  datePresets.last14Days,
  datePresets.last30Days,
  datePresets.last90Days,
  { label: 'Launch window', shortLabel: 'Launch', start: '2026-06-01', end: '2026-06-14' }
]

/** 2. Range, full display, no presets (straight to calendar) */
const fullRange = ref<DateRange>({ start: '2026-06-09', end: '2026-06-25' })

/** 3. Single date, input trigger */
const singleDate = ref<string | null>(null)
const singleHasError = ref(false)

/** 4. Single date + time */
const singleDateTime = ref<string | null>(null)

/** 5. Week mode */
const weekRange = ref<DateRange>({ start: null, end: null })

/** 6. Month mode */
const monthRange = ref<DateRange>({ start: null, end: null })

/** 7. Constraints: min/max/excluded/maxRange */
const constrainedRange = ref<DateRange>({ start: null, end: null })
const constraintExcludeDates = ['2026-07-08', '2026-07-15']

/** 8. Inline calendars */
const inlineSingle = ref<string | null>(null)
const inlineRange = ref<DateRange>({ start: null, end: null })

/** 9. Standalone TimePicker */
const shiftStart = ref<string | null>(null)
</script>

<template>
  <div class="p-8 max-w-4xl space-y-10">
    <h1 class="text-xl font-semibold">New DatePicker / Calendar / TimePicker</h1>

    <!-- 1. Sentry-style range filter -->
    <section class="space-y-3">
      <h2 class="font-semibold">1. Sentry-style range filter</h2>
      <p class="text-sm text-secondary">
        Button trigger, friendly label, mixed presets (built-ins + custom), absolute date with time range.
      </p>
      <DatePicker
        v-model="sentryRange"
        mode="range"
        :presets="sentryPresets"
        allow-absolute-date
        display-as="button"
        display-format="friendly"
        show-time-range
      />
      <pre class="demo-value">{{ sentryRange }}</pre>
    </section>

    <!-- 2. Range, full display -->
    <section class="space-y-3">
      <h2 class="font-semibold">2. Range, full display, no presets</h2>
      <p class="text-sm text-secondary">Opens straight to the calendar; closes when the range is completed.</p>
      <DatePicker
        v-model="fullRange"
        mode="range"
        display-as="button"
        display-format="full"
      />
      <pre class="demo-value">{{ fullRange }}</pre>
    </section>

    <!-- 3. Single date, input trigger -->
    <section class="space-y-3">
      <h2 class="font-semibold">3. Single date, input trigger</h2>
      <div class="max-w-xs">
        <DatePicker
          v-model="singleDate"
          mode="single"
          display-as="input"
          display-format="full"
          :has-error="singleHasError"
          placeholder="Pick a date"
        />
      </div>
      <ToggleSwitch
        v-model="singleHasError"
        label="hasError"
      />
      <pre class="demo-value">{{ singleDate ?? 'null' }}</pre>
    </section>

    <!-- 4. Single date + time -->
    <section class="space-y-3">
      <h2 class="font-semibold">4. Single date + time</h2>
      <p class="text-sm text-secondary">TimePicker below the grid; committed with Apply.</p>
      <div class="max-w-xs">
        <DatePicker
          v-model="singleDateTime"
          mode="single"
          display-as="input"
          display-format="full"
          show-time
          placeholder="Pick date & time"
        />
      </div>
      <pre class="demo-value">{{ singleDateTime ?? 'null' }}</pre>
    </section>

    <!-- 5. Week mode -->
    <section class="space-y-3">
      <h2 class="font-semibold">5. Week mode</h2>
      <p class="text-sm text-secondary">Hover highlights the week; clicking any day selects the ISO week.</p>
      <DatePicker
        v-model="weekRange"
        mode="week"
        display-as="button"
        display-format="friendly"
      />
      <pre class="demo-value">{{ weekRange }}</pre>
    </section>

    <!-- 6. Month mode -->
    <section class="space-y-3">
      <h2 class="font-semibold">6. Month mode</h2>
      <p class="text-sm text-secondary">Picks a whole month as a range.</p>
      <DatePicker
        v-model="monthRange"
        mode="month"
        display-as="button"
        display-format="full"
      />
      <pre class="demo-value">{{ monthRange }}</pre>
    </section>

    <!-- 7. Constraints -->
    <section class="space-y-3">
      <h2 class="font-semibold">7. Constraints</h2>
      <p class="text-sm text-secondary">
        minDate 2026-07-01, maxDate 2026-08-15, excluded 8 &amp; 15 July, max range 10 days.
      </p>
      <DatePicker
        v-model="constrainedRange"
        mode="range"
        display-as="button"
        display-format="full"
        min-date="2026-07-01"
        max-date="2026-08-15"
        :exclude-dates="constraintExcludeDates"
        :max-range="10"
      />
      <pre class="demo-value">{{ constrainedRange }}</pre>
    </section>

    <!-- 8. Inline calendars -->
    <section class="space-y-3">
      <h2 class="font-semibold">8. Inline Calendar (no dropdown)</h2>
      <div class="flex flex-wrap gap-8">
        <div class="demo-card">
          <div class="text-sm font-medium mb-2">Single</div>
          <DatePickerCalendar
            v-model="inlineSingle"
            mode="single"
          />
          <pre class="demo-value">{{ inlineSingle ?? 'null' }}</pre>
        </div>
        <div class="demo-card">
          <div class="text-sm font-medium mb-2">Range</div>
          <DatePickerCalendar
            v-model="inlineRange"
            mode="range"
          />
          <pre class="demo-value">{{ inlineRange }}</pre>
        </div>
      </div>
    </section>

    <!-- 9. TimePicker -->
    <section class="space-y-3">
      <h2 class="font-semibold">9. TimePicker</h2>
      <div class="max-w-40">
        <TimePicker
          v-model="shiftStart"
          placeholder="Shift start"
          :minute-step="5"
        />
      </div>
      <pre class="demo-value">{{ shiftStart ?? 'null' }}</pre>
    </section>
  </div>
</template>

<style scoped>
.demo-value {
  font-size: var(--text-xs);
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-md);
  padding: var(--s-2) var(--s-3);
  width: max-content;
  min-width: 240px;
}

.demo-card {
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-lg);
  padding: var(--s-4);
  width: max-content;
}
</style>
